import { useEffect, useRef, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Clock, LogOut } from "lucide-react";

export const IDLE_MINUTES = 30;
const IDLE_MS = IDLE_MINUTES * 60 * 1000;
const WARN_MS = 2 * 60 * 1000;
const HEARTBEAT_MS = 5 * 60 * 1000;
const ACTIVITY_EVENTS = ["pointerdown", "keydown", "touchstart", "wheel"];

// Per-device inactivity guard: warns 2 minutes before, then saves work and signs the user out.
// The server enforces the same limit (sessions idle > 30 min are rejected), this only makes it graceful.
export const useIdleGuard = ({ active, touch, onIdleLogout }) => {
  const lastActivity = useRef(Date.now());
  const lastBeat = useRef(Date.now());
  const firing = useRef(false);
  const [secondsLeft, setSecondsLeft] = useState(null);

  const markActive = useCallback(() => {
    lastActivity.current = Date.now();
    setSecondsLeft((s) => (s === null ? s : null));
  }, []);

  useEffect(() => {
    if (!active) { setSecondsLeft(null); firing.current = false; return undefined; }
    lastActivity.current = Date.now();
    lastBeat.current = Date.now();
    ACTIVITY_EVENTS.forEach((e) => window.addEventListener(e, markActive, { passive: true, capture: true }));
    const timer = setInterval(() => {
      const idle = Date.now() - lastActivity.current;
      if (idle >= IDLE_MS) {
        if (!firing.current) { firing.current = true; onIdleLogout(); }
        return;
      }
      if (idle >= IDLE_MS - WARN_MS) setSecondsLeft(Math.ceil((IDLE_MS - idle) / 1000));
      // heartbeat only while the user is really active (keeps the server-side idle clock in step)
      if (lastActivity.current > lastBeat.current && Date.now() - lastBeat.current >= HEARTBEAT_MS) {
        lastBeat.current = Date.now();
        touch();
      }
    }, 1000);
    return () => {
      ACTIVITY_EVENTS.forEach((e) => window.removeEventListener(e, markActive, { capture: true }));
      clearInterval(timer);
    };
  }, [active, markActive, onIdleLogout, touch]);

  const stay = useCallback(() => { markActive(); lastBeat.current = Date.now(); touch(); }, [markActive, touch]);
  return { secondsLeft, stay };
};

export const IdleWarningDialog = ({ secondsLeft, onStay, onLogout }) => (
  <Dialog open={secondsLeft !== null} onOpenChange={(o) => { if (!o) onStay(); }}>
    <DialogContent className="max-w-sm bg-th-card rounded-2xl p-5" data-testid="idle-warning-dialog" aria-describedby={undefined}>
      <DialogHeader>
        <DialogTitle className="flex items-center gap-2 text-base"><Clock className="w-5 h-5 text-amber-600" /> Still there?</DialogTitle>
      </DialogHeader>
      <p className="text-sm text-th-muted">
        You'll be signed out in <span className="font-bold text-cream-900 tabular-nums" data-testid="idle-seconds-left">{Math.floor((secondsLeft || 0) / 60)}:{String((secondsLeft || 0) % 60).padStart(2, "0")}</span> for inactivity.
        Your cart will be saved and your open shift stays open.
      </p>
      <div className="flex gap-2 pt-2">
        <Button variant="outline" onClick={onLogout} className="h-10 border-th-border" data-testid="idle-sign-out"><LogOut className="w-4 h-4 mr-1" /> Sign out now</Button>
        <Button onClick={onStay} className="h-10 flex-1 font-semibold" data-testid="idle-stay">I'm still here</Button>
      </div>
    </DialogContent>
  </Dialog>
);
